import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box } from "@mui/material";

const BAR_COUNT = 64;

// Generate a static waveform shape that looks like a real audio waveform
function generateWaveformShape(count) {
  const bars = [];
  for (let i = 0; i < count; i++) {
    const pos = i / count;
    // Envelope: quieter at edges, louder in middle sections with variation
    const envelope = Math.sin(pos * Math.PI) * 0.6 + 0.4;
    // Multiple sine waves at different frequencies for organic look
    const wave =
      Math.sin(pos * 12.5) * 0.2 +
      Math.sin(pos * 23.7) * 0.15 +
      Math.sin(pos * 47.3) * 0.1 +
      Math.sin(pos * 91.1) * 0.05;
    // Random variation for texture
    const noise = (Math.sin(i * 137.5) * 0.5 + 0.5) * 0.25;
    const value = Math.max(0.08, Math.min(1, envelope * (0.5 + wave + noise)));
    bars.push(value);
  }
  return bars;
}

function AudioEqualizer({ loading, isPlaying }) {
  const [phase, setPhase] = useState(0);
  const animFrameRef = useRef(null);
  const lastUpdateRef = useRef(0);

  // Static waveform shape — doesn't change per render
  const waveformShape = useMemo(() => generateWaveformShape(BAR_COUNT), []);

  useEffect(() => {
    if (loading || !isPlaying) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      return;
    }

    const animate = (timestamp) => {
      // Update ~20fps for smooth but not excessive animation
      if (timestamp - lastUpdateRef.current > 50) {
        lastUpdateRef.current = timestamp;
        setPhase((p) => p + 0.08);
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPlaying, loading]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "60%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "2px",
        }}
      >
        {waveformShape.map((baseHeight, i) => {
          // Animate each bar with a travelling wave effect
          const pulse = isPlaying && !loading
            ? 0.3 * Math.sin(phase + i * 0.25) + 0.15 * Math.sin(phase * 1.7 + i * 0.4)
            : 0;
          const height = loading
            ? 8
            : Math.max(8, (baseHeight + pulse) * 100);

          return (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${Math.min(100, height)}%`,
                minHeight: "4px",
                borderRadius: "2px",
                backgroundColor: isPlaying ? "#7c3aed" : "#6b7280",
                opacity: loading ? 0.3 : isPlaying ? 0.9 : 0.5,
                transition: loading ? "height 0.3s ease" : "none",
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default AudioEqualizer;
